import { isEmpty } from 'lodash';
import { TxnFilterQuery } from '../../graphql/types';
import { TxnFilterQueryWithLocks } from './types';

const DELETE_TXN_STORAGE_KEY = 'doNotAskAgainToConfirmTxnDeletion';
const LOCKED_FILTERS_STORAGE_KEY = 'allTransactionsLockedFilters';

export const SEARCH_FILTER_KEYS: Array<
  keyof Pick<
    TxnFilterQueryWithLocks,
    | 'description'
    | 'fromAddress'
    | 'location'
    | 'methodId'
    | 'reconIdentifier'
    | 'toAddress'
    | 'transferTopicFingerprint'
    | 'txnHash'
    | 'txnTopicFingerprint'
  >
> = [
  'description',
  'fromAddress',
  'location',
  'methodId',
  'reconIdentifier',
  'toAddress',
  'transferTopicFingerprint',
  'txnHash',
  'txnTopicFingerprint',
];
export type SearchFilterKey = (typeof SEARCH_FILTER_KEYS)[number];

export const SINGLE_VALUE_FILTER_KEYS: Array<
  keyof Pick<
    TxnFilterQueryWithLocks,
    | 'txnType'
    | 'importType'
    | 'imports'
    | 'accounts'
    | 'vendors'
    | 'accountCredits'
    | 'accountDebits'
    | 'date'
    | 'editedAt'
  >
> = [
  'txnType',
  'importType',
  'imports',
  'accounts',
  'vendors',
  'accountCredits',
  'accountDebits',
  'date',
  'editedAt',
];

export const ARRAY_FILTER_KEYS: Array<keyof Pick<TxnFilterQueryWithLocks, 'currency' | 'boolean'>> = [
  'currency',
  'boolean',
];

export const doNotAskAgainToConfirmTxnDeletion = () =>
  window.localStorage.setItem(DELETE_TXN_STORAGE_KEY, 'true');

export const shouldAskToConfirmTxnDeletion = () => {
  return window.localStorage.getItem(DELETE_TXN_STORAGE_KEY) !== 'true';
};

const MERGE_TXNS_SESSION_STORAGE_KEY = 'doNotAskAgainToConfirmMergeTxns';

export const doNotAskAgainToConfirmMergeTxns = () =>
  window.localStorage.setItem(MERGE_TXNS_SESSION_STORAGE_KEY, 'true');

export const shouldAskToConfirmMergeTxns = () => {
  return window.localStorage.getItem(MERGE_TXNS_SESSION_STORAGE_KEY) !== 'true';
};

const CREATE_MOVEMENT_FROM_SPAM_STORAGE_KEY = 'doNotAskAgainToConfirmCreateMovementFromSpam';

export const doNotAskAgainToConfirmCreateMovementFromSpam = () =>
  window.localStorage.setItem(CREATE_MOVEMENT_FROM_SPAM_STORAGE_KEY, 'true');

export const shouldAskToConfirmCreateMovementFromSpam = () => {
  return window.localStorage.getItem(CREATE_MOVEMENT_FROM_SPAM_STORAGE_KEY) !== 'true';
};

export const removeLockedStatusFromFilterQuery = (filterQuery: TxnFilterQueryWithLocks): TxnFilterQuery => {
  const sanitized = { ...filterQuery };

  if (sanitized.txnType && 'locked' in sanitized.txnType) {
    const { locked: _l, ...rest } = sanitized.txnType;
    sanitized.txnType = rest;
  }
  if (sanitized.importType && 'locked' in sanitized.importType) {
    const { locked: _l, ...rest } = sanitized.importType;
    sanitized.importType = rest;
  }
  if (sanitized.imports && 'locked' in sanitized.imports) {
    const { locked: _l, ...rest } = sanitized.imports;
    sanitized.imports = rest;
  }
  if (sanitized.accounts && 'locked' in sanitized.accounts) {
    const { locked: _l, ...rest } = sanitized.accounts;
    sanitized.accounts = rest;
  }
  if (sanitized.vendors && 'locked' in sanitized.vendors) {
    const { locked: _l, ...rest } = sanitized.vendors;
    sanitized.vendors = rest;
  }
  if (sanitized.accountCredits && 'locked' in sanitized.accountCredits) {
    const { locked: _l, ...rest } = sanitized.accountCredits;
    sanitized.accountCredits = rest;
  }
  if (sanitized.accountDebits && 'locked' in sanitized.accountDebits) {
    const { locked: _l, ...rest } = sanitized.accountDebits;
    sanitized.accountDebits = rest;
  }
  if (sanitized.date && 'locked' in sanitized.date) {
    const { locked: _l, ...rest } = sanitized.date;
    sanitized.date = rest;
  }
  if (sanitized.editedAt && 'locked' in sanitized.editedAt) {
    const { locked: _l, ...rest } = sanitized.editedAt;
    sanitized.editedAt = rest;
  }

  if (sanitized.currency && Array.isArray(sanitized.currency)) {
    (sanitized as TxnFilterQuery).currency = sanitized.currency.map((filter) => {
      if ('locked' in filter) {
        const { locked: _l, ...rest } = filter;
        return rest;
      }
      return filter;
    });
  }
  if (sanitized.boolean && Array.isArray(sanitized.boolean)) {
    (sanitized as TxnFilterQuery).boolean = sanitized.boolean.map((filter) => {
      if ('locked' in filter) {
        const { locked: _l, ...rest } = filter;
        return rest;
      }
      return filter;
    });
  }

  SEARCH_FILTER_KEYS.forEach((key) => {
    const filters = sanitized[key];
    if (filters && Array.isArray(filters)) {
      (sanitized as TxnFilterQuery)[key] = filters.map((filter) => {
        if ('locked' in filter) {
          const { locked: _l, ...rest } = filter;
          return rest;
        }
        return filter;
      }) as TxnFilterQuery[typeof key];
    }
  });

  return sanitized as TxnFilterQuery;
};

export const areAllFiltersLocked = (filterQuery: TxnFilterQueryWithLocks): boolean => {
  const filters: unknown[] = [];

  SINGLE_VALUE_FILTER_KEYS.forEach((key) => {
    if (filterQuery[key]) filters.push(filterQuery[key]);
  });

  [...ARRAY_FILTER_KEYS, ...SEARCH_FILTER_KEYS].forEach((key) => {
    const arrayFilters = filterQuery[key];
    if (arrayFilters && Array.isArray(arrayFilters)) {
      filters.push(...arrayFilters);
    }
  });

  if (filters.length === 0) return false;

  return filters.every((filter) => {
    if (filter && typeof filter === 'object' && 'locked' in filter) {
      return filter.locked === true;
    }
    return false;
  });
};

export const getLockedFiltersFromStorage = (): Partial<TxnFilterQueryWithLocks> => {
  try {
    const stored = window.localStorage.getItem(LOCKED_FILTERS_STORAGE_KEY);
    if (!stored) return {};
    return JSON.parse(stored) as Partial<TxnFilterQueryWithLocks>;
  } catch {
    return {};
  }
};

export const saveLockedFiltersToStorage = (filterQuery: TxnFilterQueryWithLocks): void => {
  const lockedFilters: Partial<TxnFilterQueryWithLocks> = {};

  if (filterQuery.txnType?.locked) lockedFilters.txnType = filterQuery.txnType;
  if (filterQuery.importType?.locked) lockedFilters.importType = filterQuery.importType;
  if (filterQuery.imports?.locked) lockedFilters.imports = filterQuery.imports;
  if (filterQuery.vendors?.locked) lockedFilters.vendors = filterQuery.vendors;
  if (filterQuery.accountCredits?.locked) lockedFilters.accountCredits = filterQuery.accountCredits;
  if (filterQuery.accountDebits?.locked) lockedFilters.accountDebits = filterQuery.accountDebits;
  if (filterQuery.date?.locked) lockedFilters.date = filterQuery.date;
  if (filterQuery.editedAt?.locked) lockedFilters.editedAt = filterQuery.editedAt;

  if (filterQuery.currency && Array.isArray(filterQuery.currency)) {
    const locked = filterQuery.currency.filter((f) => f.locked);
    if (locked.length > 0) lockedFilters.currency = locked;
  }

  if (filterQuery.boolean && Array.isArray(filterQuery.boolean)) {
    const locked = filterQuery.boolean.filter((f) => f.locked);
    if (locked.length > 0) lockedFilters.boolean = locked;
  }

  SEARCH_FILTER_KEYS.forEach((key) => {
    const filters = filterQuery[key];
    if (filters && Array.isArray(filters)) {
      const locked = filters.filter((f) => f.locked);
      if (locked.length > 0) {
        lockedFilters[key] = locked as TxnFilterQueryWithLocks[typeof key];
      }
    }
  });

  if (isEmpty(lockedFilters)) {
    window.localStorage.removeItem(LOCKED_FILTERS_STORAGE_KEY);
  } else {
    window.localStorage.setItem(LOCKED_FILTERS_STORAGE_KEY, JSON.stringify(lockedFilters));
  }
};

export const hasAnyLockedFilters = (filterQuery: TxnFilterQueryWithLocks): boolean => {
  if (SINGLE_VALUE_FILTER_KEYS.some((key) => filterQuery[key]?.locked)) return true;

  if (ARRAY_FILTER_KEYS.some((key) => filterQuery[key]?.some((f) => f.locked))) return true;

  return SEARCH_FILTER_KEYS.some((key) => {
    const filters = filterQuery[key];
    return filters && Array.isArray(filters) && filters.some((f) => f.locked);
  });
};

export const mergeFiltersWithStoredLocks = (
  urlFilters: Partial<TxnFilterQueryWithLocks>,
  storedFilters: Partial<TxnFilterQueryWithLocks>,
): Partial<TxnFilterQueryWithLocks> => {
  const merged: Partial<TxnFilterQueryWithLocks> = { ...storedFilters, ...urlFilters };

  if (urlFilters.currency || storedFilters.currency) {
    merged.currency = [...(storedFilters.currency || []), ...(urlFilters.currency || [])];
  }
  if (urlFilters.boolean || storedFilters.boolean) {
    merged.boolean = [...(storedFilters.boolean || []), ...(urlFilters.boolean || [])];
  }

  SEARCH_FILTER_KEYS.forEach((key) => {
    if (urlFilters[key] || storedFilters[key]) {
      const stored = storedFilters[key] || [];
      const url = urlFilters[key] || [];
      merged[key] = [...stored, ...url] as TxnFilterQueryWithLocks[typeof key];
    }
  });

  return merged;
};
